import React, { useEffect, useState } from 'react';
import { Tooltip as PrimeTooltip, type TooltipProps as PrimeTooltipProps } from 'primereact/tooltip';
import styles from './Tooltip.module.scss';
import 'primeicons/primeicons.css';
import tooltip_img from '../../../assets/pointer.svg';

function escapeSelectorAttributeValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }

  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export interface TooltipProps extends Omit<PrimeTooltipProps, 'position'> {
  tooltipText?: string;
  arrowPosition?: 'Top' | 'Bottom' | 'Left' | 'Right';
  arrow?: boolean;
  pointer?: boolean;
  tooltipTarget: string;
  tooltipProps?: Partial<Omit<PrimeTooltipProps, 'position'>>;
}

/**
 * Custom Tooltip component using PrimeReact's Tooltip. It supports tooltip positioning, arrow toggle, and pointer visuals.
 *
 * ## Import path
 * ```tsx
 * import { Tooltip } from '@kpmg-global-audit-emu/react-ui-kit/atoms/Tooltip';
 * ```
 * @param root0 - Tooltip configuration props
 * @param root0.tooltipText - Text shown inside the tooltip
 * @param root0.arrowPosition - Tooltip arrow position (Top, Bottom, Left, Right)
 * @param root0.arrow - Whether to show the arrow
 * @param root0.pointer - Whether to show the custom pointer class
 * @param root0.tooltipTarget - unique target id for linking pointer with tooltip
 * @param root0.tooltipProps - Additional props to pass to the underlying PrimeReact Tooltip component
 * @returns A styled tooltip target element with configurable behavior
 */
export const Tooltip: React.FC<TooltipProps> = ({
  tooltipText = '',
  arrowPosition = 'Bottom',
  arrow,
  pointer,
  tooltipTarget,
  tooltipProps,
  ...props
}) => {
  type PrimeTooltipPosition = 'top' | 'bottom' | 'left' | 'right';
  const [position, setPosition] = useState<PrimeTooltipPosition>(
    arrowPosition.toLowerCase() as PrimeTooltipPosition
  );
  useEffect(() => {
    setPosition(arrowPosition.toLowerCase() as PrimeTooltipPosition);
  }, [arrowPosition]);

  const arrowPositionClass = `tooltip-${arrowPosition.toLowerCase().replace(/ /g, '-')}`;

  const customClass = `${styles[arrowPositionClass]} ${arrow ? styles['show-arrow'] : styles['hide-arrow']}`;
  const pointerClass = pointer ? styles['tooltip-pointer'] : '';
  const escapedTooltipTarget = escapeSelectorAttributeValue(tooltipTarget);

  const mergedTooltipProps: PrimeTooltipProps = {
    ...props,
    ...tooltipProps,
    position,
  };

  return (
    <div className={styles['tooltip-container']}>
      <PrimeTooltip
        {...mergedTooltipProps}
        key={position}
        target={`[data-target="${escapedTooltipTarget}"]`}
        content={tooltipText || 'This is a tooltip'}
        className={`${styles.tooltip} ${customClass} ${pointerClass} ${mergedTooltipProps.className ?? ''
          }`.trim()}
      />
      {pointer && (
        <img
          src={tooltip_img}
          alt={tooltipText !== '' ? tooltipText : 'Tooltip Pointer'}
          data-target={tooltipTarget}
        />
      )}
    </div>
  );
};